/**
 * Portal keys and payload building for "Add credential".
 *
 * `credentials.portal_name` is a lookup key, not a display name. The
 * orchestrator leases a login with an exact, case-sensitive match
 * (`Credential.portal_name == portal` in
 * shared/python/flow_shared/runner_leases.py) against the `portal`
 * value in the workflow definition, and the email-MFA config is keyed
 * the same way. A credential saved as "Medpoint" is never found by a
 * workflow that asks for `medpoint`; the job just waits for a login.
 *
 * So the form stores the key and shows the label.
 */
import type { CreateCredentialPayload } from "./api";

export interface PortalOption {
  /** Stored in `portal_name`. Must equal the workflow's `portal`. */
  key: string;
  label: string;
}

/** Keys used by workflow definitions in this repo. */
export const PORTAL_OPTIONS: readonly PortalOption[] = [
  // apps/orchestrator/flow_orchestrator/seed_medpoint_auth_check.py: PORTAL_NAME
  { key: "medpoint", label: "Medpoint" },
  // workflows/laoss/preferred-ipa-referral.yaml: credentials[].portal
  { key: "preferred-ipa", label: "Preferred IPA" },
];

export const OTHER_PORTAL = "__other__";

const PORTAL_KEY = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/** Trim and lowercase. Keys in workflow definitions are lowercase slugs. */
export function normalizePortalKey(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isPortalKey(value: string): boolean {
  return PORTAL_KEY.test(value);
}

/** Label for a stored portal name: the known label, or the name itself. */
export function portalLabel(portalName: string): string {
  return PORTAL_OPTIONS.find((p) => p.key === portalName)?.label ?? portalName;
}

export interface CredentialFormValues {
  /** A key from PORTAL_OPTIONS, OTHER_PORTAL, or "" when nothing is chosen. */
  portalChoice: string;
  /** Free-text key, used when `portalChoice` is OTHER_PORTAL. */
  customPortal: string;
  entityCode: string;
  username: string;
  password: string;
  mfa: "none" | "totp" | "email";
  totpSecret: string;
  mfaMailbox: string;
}

export type BuildResult =
  | { ok: true; payload: CreateCredentialPayload }
  | { ok: false; error: string };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function buildCreatePayload(v: CredentialFormValues): BuildResult {
  const portal =
    v.portalChoice === OTHER_PORTAL ? normalizePortalKey(v.customPortal) : v.portalChoice;
  if (!portal) return { ok: false, error: "Select a portal." };
  if (!isPortalKey(portal)) {
    return {
      ok: false,
      error: "Portal key can use lowercase letters, digits, hyphens and underscores only.",
    };
  }
  if (!v.entityCode) return { ok: false, error: "Select an entity." };
  if (!v.username.trim()) return { ok: false, error: "Enter the username." };
  if (!v.password) return { ok: false, error: "Enter the password." };

  // The orchestrator lowercases the mailbox before matching; store it that way.
  const mailbox = v.mfaMailbox.trim().toLowerCase();
  if (v.mfa === "totp" && !v.totpSecret.trim()) {
    return { ok: false, error: "Enter the TOTP seed." };
  }
  if (v.mfa === "email" && !EMAIL.test(mailbox)) {
    return { ok: false, error: "Enter the mailbox that receives the code, as an email address." };
  }

  return {
    ok: true,
    payload: {
      entity_code: v.entityCode,
      portal_name: portal,
      username: v.username.trim(),
      password: v.password,
      mfa_type: v.mfa,
      totp_secret: v.mfa === "totp" ? v.totpSecret.trim() : undefined,
      mfa_mailbox: v.mfa === "email" ? mailbox : undefined,
    },
  };
}
